import type { AxiosResponse } from "axios";

import { deleteData, getData, postData } from "@/apis/apiInstance";

import type {
  AddAuditSessionPayloadType,
  DownloadFixedAssetReportPayloadType,
  DownloadPhysicalVerificationReportPayloadType,
  FixedAssetAddUpdateType,
  FixedAssetGetFinacAssetType,
  FixedAssetHistoryPayloadType,
  FixedAssetSingleType,
  FixedAssetVerificationType,
  GetAuditSessionPayloadType,
  GetFARUsersHistoryPayloadType,
  UpdateAuditSessionPayloadType,
} from "@/commonTypes/fixedAssetRegisterTypes";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function addFixedAssetApi(
  data: FixedAssetAddUpdateType
): Promise<AxiosResponse> {
  const { token, fixedAssetRegisterForm } = data;

  const finalData = { ...fixedAssetRegisterForm };

  return postData(`invoice-generation/fixed/asset/add/`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getFinacAssetIdApi(
  data: FixedAssetGetFinacAssetType
): Promise<AxiosResponse> {
  const { token, client_code, company_name } = data;
  const finalParams = {
    company_name,
    client_code,
  };
  return await getData(`invoice-generation/fixed/asset/id/generate`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getFixedAssetRegisterListingApi(
  data: any
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    limit,
    offset,
    company_name,
    sort_by,
    sort_by_value,
    searchValue,
    condition,
    location,
    qr_code,
    is_verified,
    department,
    is_assigned,
  } = data;
  const finalParams = {
    company_name,
    client_code,
    sort_by_value,
    sort_by,
    search_term: searchValue,
    limit: limit,
    offset: offset,
    condition,
    location,
    qr_code,
    is_verified,
    department,
    is_assigned,
  };
  return await getData(`invoice-generation/fixed/asset/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getSingleFinacAssetApi(
  data: FixedAssetSingleType
): Promise<AxiosResponse> {
  const { token, client_code, id } = data;
  const finalParams = {
    finac_asset_id: id,
    client_code,
  };
  return await getData(`invoice-generation/fixed/asset`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function updateFixedAssetApi(data: any): Promise<AxiosResponse> {
  const { token, fixedAssetRegisterForm } = data;

  const finalData = { ...fixedAssetRegisterForm };

  return postData(`invoice-generation/fixed/asset/update/`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function deleteFixedAssetApi(data: any): Promise<AxiosResponse> {
  const { token, asset_id } = data;

  const finalData = { finac_asset_id: asset_id };

  return deleteData(`invoice-generation/fixed/asset/delete`, {
    params: {
      ...finalData,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function fixedAssetBulkUploadApi(
  data: any
): Promise<AxiosResponse> {
  const { token, client_code, fileData, company_name } = data;

  const formData = new FormData();
  formData.append("client_code", client_code);
  formData.append("company_name", company_name);
  formData.append("file", fileData);

  return postData(`invoice-generation/fixed/asset/upload/`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postFixedAssetVerification(
  data: FixedAssetVerificationType
): Promise<AxiosResponse> {
  const { token, condition, fixed_asset_id, client_code } = data;

  const finalData = { condition, fixed_asset_id, client_code };

  return postData(
    `invoice-generation/fixed/asset/add/verification/`,
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getFixedAssetHistoryApi(
  data: FixedAssetHistoryPayloadType
): Promise<AxiosResponse> {
  const { token, fixedAssetId } = data;
  const finalParams = {
    fixed_asset_id: fixedAssetId,
  };
  return await getData(`invoice-generation/fixed/asset/verification/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

// audit session
export async function getAuditSessionListApi(
  data: GetAuditSessionPayloadType
): Promise<AxiosResponse> {
  const { token, client_code } = data;
  return await getData(`/invoice-generation/audit/session/list`, {
    params: {
      client_code,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function createAuditSessionApi(
  data: AddAuditSessionPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    start_date,
    end_date,
    client_code,
    audit_team,
    financial_year,
  } = data;
  const finalData = {
    start_date,
    end_date,
    client_code: client_code,
    audit_team,
    financial_year,
  };

  return await postData(`/invoice-generation/audit/session/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateAuditSessionApi(
  data: UpdateAuditSessionPayloadType
): Promise<AxiosResponse> {
  const { token, start_date, end_date, audit_team, status, audit_session_id } =
    data;
  const finalData = {
    start_date,
    end_date,
    status: status,
    audit_team,
    audit_session_id,
  };

  return await postData(`/invoice-generation/audit/session/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function downloadFixedAssetReportApi(
  data: DownloadFixedAssetReportPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    condition,
    location,
    search_term,
    qr_code,
    sort_by,
    sort_by_value,
  } = data;
  return await getData(`/invoice-generation/fixed/asset/download`, {
    params: {
      client_code,
      condition,
      location,
      search_term,
      qr_code,
      sort_by_value,
      sort_by,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadPhysicalVerificationReportApi(
  data: DownloadPhysicalVerificationReportPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    condition,
    is_verified,
    start_date,
    end_date,
    department,
    sort_by,
    sort_by_value,
    location,
    search_term,
  } = data;
  return await getData(
    `/invoice-generation/fixed/asset/verification/download`,
    {
      params: {
        client_code,
        condition,
        location,
        search_term,
        is_verified,
        start_date,
        end_date,
        department,
        sort_by_value,
        sort_by,
      },
      responseType: "blob",
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}

export async function getFARUsersHistory(
  data: GetFARUsersHistoryPayloadType
): Promise<AxiosResponse> {
  const { token, farId } = data;
  return await getData(`invoice-generation/fixed/asset/user/history`, {
    params: {
      far_id: farId,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
